/**
 * The Orb — the one living element in a still interface (REDESIGN.md §4).
 *
 * A soft breathing core behind a vector mesh: 110 points on a fibonacci
 * lattice, each linked to its three nearest neighbours, drawn on a canvas and
 * rotated slowly. Every constant here — the point count, the radius, the
 * rotation speeds, the opacity ramps, the analyser settings, the chime's
 * frequencies and envelopes — is copied from design/screens-reference.html
 * rather than reinvented, because the reference is the design.
 *
 * It has no input field. It is presence, not a chatbot.
 */

import { salutation, situational, readout as readoutLine, RECEIVING } from '../content/orbLines.js'
import { text } from '../js/html.js'

// ---- the lattice (reference: N, R, C) -------------------------------------

const POINTS = 110
const MESH_RADIUS = 148
const CENTRE = 210
const NEIGHBOURS = 3

/** Points of a fibonacci sphere: an even scatter without a seam or poles. */
function lattice() {
  const golden = Math.PI * (3 - Math.sqrt(5))
  const points = []

  for (let i = 0; i < POINTS; i += 1) {
    const y = 1 - (i / (POINTS - 1)) * 2
    const radius = Math.sqrt(1 - y * y)
    const theta = golden * i

    points.push({ x: Math.cos(theta) * radius, y, z: Math.sin(theta) * radius })
  }

  return points
}

/** Each point to its three nearest, de-duplicated so an edge is drawn once. */
function edges(points) {
  const links = []

  for (let i = 0; i < POINTS; i += 1) {
    const distances = []

    for (let j = 0; j < POINTS; j += 1) {
      if (i === j) continue

      const dx = points[i].x - points[j].x
      const dy = points[i].y - points[j].y
      const dz = points[i].z - points[j].z

      distances.push([dx * dx + dy * dy + dz * dz, j])
    }

    distances.sort((a, b) => a[0] - b[0])

    for (let k = 0; k < NEIGHBOURS; k += 1) {
      const j = distances[k][1]

      if (i < j) links.push([i, j])
    }
  }

  return links
}

// ---- typing ----------------------------------------------------------------

/** ~24 characters a second, and no cursor once it has finished. */
const CHARS_PER_SECOND = 24
const SITUATIONAL_DELAY = 2500

function typeInto(element, line, { reduced, onDone }) {
  if (reduced) {
    element.textContent = line
    onDone?.()

    return () => {}
  }

  element.textContent = ''
  element.classList.add('orb__line--typing')

  let index = 0
  const timer = window.setInterval(() => {
    index += 1
    element.textContent = line.slice(0, index)

    if (index >= line.length) {
      window.clearInterval(timer)
      element.classList.remove('orb__line--typing')
      onDone?.()
    }
  }, 1000 / CHARS_PER_SECOND)

  return () => {
    window.clearInterval(timer)
    element.classList.remove('orb__line--typing')
  }
}

/**
 * Projects the lattice and draws it. Lifted out of the Orb's own frame loop so
 * the orbit menu's central orb can run the same mesh without the audio graph,
 * the typing or the chime behind it (NAVIGATION.md §5). Every constant stays
 * where it was; only the inputs became arguments.
 */
function drawMesh(context2d, points, links, { time, bass, mid, high, tap, spectrum, playing, reduced }) {
  const speed = playing ? 0.00034 : 0.00011
  const ry = reduced ? 0.6 : time * speed + (playing ? mid * 0.5 : 0)
  const rx = reduced ? 0.4 : 0.35 + Math.sin(time * 0.00005) * 0.18 + (playing ? bass * 0.35 : 0)

  const cy = Math.cos(ry)
  const sy = Math.sin(ry)
  const cx = Math.cos(rx)
  const sx = Math.sin(rx)

  const breath = (reduced ? 1 : 1 + 0.02 * Math.sin(time * 0.0013)) + bass * 0.28 + tap * 0.14

  const projected = points.map((point, index) => {
    let amplitude = 1

    if (spectrum) {
      const f = spectrum[(index % 80) + 4] / 255
      amplitude = 1 + f * f * 0.3
    }

    const x = point.x * amplitude
    const y = point.y * amplitude
    const z = point.z * amplitude

    const X = x * cy + z * sy
    let Z = -x * sy + z * cy
    const Y = y * cx - Z * sx
    Z = y * sx + Z * cx

    const scale = MESH_RADIUS * breath * (1 / (1.6 - Z * 0.4))

    return { X: CENTRE + X * scale, Y: CENTRE + Y * scale, z: Z }
  })

  const brightness = 1 + mid * 2.4 + high * 1.2 + tap * 0.8

  for (const [i, j] of links) {
    const a = projected[i]
    const b = projected[j]
    const depth = (a.z + b.z) / 2
    const alpha = Math.min((0.08 + ((depth + 1) / 2) * 0.34) * brightness, 0.85)

    context2d.strokeStyle = `rgba(23, 190, 187, ${alpha.toFixed(3)})`
    context2d.lineWidth = depth > 0 ? 1.1 : 0.7
    context2d.beginPath()
    context2d.moveTo(a.X, a.Y)
    context2d.lineTo(b.X, b.Y)
    context2d.stroke()
  }

  for (let i = 0; i < projected.length; i += 1) {
    const point = projected[i]
    let radius = point.z > 0 ? 1.9 : 1.2

    if (spectrum) radius += (spectrum[(i % 80) + 4] / 255) * 2.2

    const alpha = Math.min((0.15 + ((point.z + 1) / 2) * 0.65) * (1 + mid * 1.2), 1)

    context2d.fillStyle = `rgba(14, 143, 140, ${alpha.toFixed(3)})`
    context2d.beginPath()
    context2d.arc(point.X, point.Y, radius, 0, 7)
    context2d.fill()
  }
}

// ---- the component ---------------------------------------------------------

/**
 * Mounts an Orb into a container.
 *
 * `deliver` is the difference between the Orb *holding* a line and *saying*
 * one: a message met before is simply the line under the Orb, while a new one
 * arrives with the chime, or with its own recorded voice when it has one.
 *
 * @param {HTMLElement} container
 * @param {{
 *   firstName?: string,
 *   state?: 'idle'|'signal'|'active',
 *   message?: string|null,
 *   audioUrl?: string|null,
 *   deliver?: boolean,
 *   onDelivered?: () => void,
 *   context?: object,
 *   readout?: object,
 *   greet?: boolean,
 * }} options
 */
export function mountOrb(container, options = {}) {
  const {
    firstName = '',
    state = 'idle',
    message = null,
    audioUrl = null,
    deliver = false,
    onDelivered = null,
    context = {},
    readout: readoutContext = {},
    greet = true,
  } = options

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // A button, not a div with a listener.
  //
  // Browsers treat an arbitrary element that happens to have a click handler as
  // something they have to explain — Safari and Chrome both flash their own
  // highlight over it, and on a circle that highlight is a square. A real
  // control gets the treatment every other control in the app gets.
  //
  // It is also simply correct: this is the only interactive thing on Home, and
  // until now it could not be reached by keyboard or announced by a screen
  // reader at all.
  container.innerHTML = `
    <button type="button" class="orb orb--${text(state)}" data-orb aria-label="Replay the message">
      <span class="orb__ripple" aria-hidden="true"></span>
      <span class="orb__core" aria-hidden="true"></span>
      <canvas class="orb__mesh" width="420" height="420" aria-hidden="true"></canvas>
    </button>
    <div class="orb-say">
      <p class="orb__greeting" data-orb-greeting></p>
      <p class="orb__line" data-orb-line></p>
      <p class="orb__readout">
        <span class="orb__led" aria-hidden="true"></span>
        <span data-orb-readout>${text(readoutLine(readoutContext))}</span>
      </p>
    </div>
  `

  const orbEl = container.querySelector('[data-orb]')
  const canvas = container.querySelector('.orb__mesh')
  const greetingEl = container.querySelector('[data-orb-greeting]')
  const lineEl = container.querySelector('[data-orb-line]')
  const readoutEl = container.querySelector('[data-orb-readout]')

  const points = lattice()
  const links = edges(points)
  const context2d = canvas.getContext('2d')

  // The line the Orb is currently holding; a tap replays this one.
  const currentLine = message ?? situational(context)
  const restingReadout = readoutLine(readoutContext)

  let tap = 0
  let playing = false
  let mode = state
  let frameHandle = null
  let stopTyping = () => {}
  let timers = []

  // ---- audio ---------------------------------------------------------------

  let audioContext = null
  let analyser = null
  let frequencies = null

  /**
   * Prepares the audio graph, or returns null when the browser will not let it
   * run. Autoplay policy suspends a context created without a user gesture, and
   * resuming it is a promise that simply never settles into 'running' — so this
   * is awaited, and a refusal is reported rather than assumed away. The Orb
   * still types the line; the sound arrives on the first tap.
   */
  async function ensureAudio() {
    const Ctor = window.AudioContext ?? window.webkitAudioContext

    if (!Ctor) return null

    audioContext = audioContext ?? new Ctor()

    if (audioContext.state === 'suspended') {
      try {
        await audioContext.resume()
      } catch {
        // Refused; state stays suspended and the check below catches it.
      }
    }

    if (audioContext.state !== 'running') {
      return null
    }

    analyser = audioContext.createAnalyser()
    analyser.fftSize = 256
    analyser.smoothingTimeConstant = 0.62
    frequencies = new Uint8Array(analyser.frequencyBinCount)

    const master = audioContext.createGain()
    master.gain.value = 0.16
    master.connect(analyser)
    analyser.connect(audioContext.destination)

    return master
  }

  function beginPlaying(durationMs) {
    playing = true
    mode = 'active'
    orbEl.className = 'orb orb--active'
    readoutEl.textContent = RECEIVING

    timers.push(
      window.setTimeout(() => {
        playing = false
        mode = state
        orbEl.className = `orb orb--${state}`
        readoutEl.textContent = restingReadout
      }, durationMs),
    )
  }

  /**
   * The signature chime, generated rather than fetched: a warm sine pad, soft
   * low pulses and sparse high blips. Frequencies and envelopes are the
   * reference's; changing them changes what the app sounds like.
   */
  async function playTransmission() {
    if (playing) return

    const master = await ensureAudio()

    if (master === null) return

    const t0 = audioContext.currentTime
    const duration = 8

    // Pad: A3, C#4, E4, entering one after another.
    for (const [frequency, at, length] of [
      [220, 0, duration],
      [277.18, 0.4, duration - 0.4],
      [329.63, 0.8, duration - 0.8],
    ]) {
      const oscillator = audioContext.createOscillator()
      const gain = audioContext.createGain()

      oscillator.type = 'sine'
      oscillator.frequency.value = frequency
      gain.gain.setValueAtTime(0, t0 + at)
      gain.gain.linearRampToValueAtTime(0.5, t0 + at + 1.6)
      gain.gain.setValueAtTime(0.5, t0 + at + length - 2)
      gain.gain.linearRampToValueAtTime(0, t0 + at + length)
      oscillator.connect(gain)
      gain.connect(master)
      oscillator.start(t0 + at)
      oscillator.stop(t0 + at + length)
    }

    // Low pulses: the heartbeat under the pad.
    for (let k = 0; k < 8; k += 1) {
      const at = 0.5 + k * 0.95
      const oscillator = audioContext.createOscillator()
      const gain = audioContext.createGain()

      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(90, t0 + at)
      oscillator.frequency.exponentialRampToValueAtTime(45, t0 + at + 0.3)
      gain.gain.setValueAtTime(0, t0 + at)
      gain.gain.linearRampToValueAtTime(0.9, t0 + at + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + at + 0.5)
      oscillator.connect(gain)
      gain.connect(master)
      oscillator.start(t0 + at)
      oscillator.stop(t0 + at + 0.6)
    }

    // Blips: sparse, high, never a melody.
    for (const [at, frequency] of [
      [0.9, 880],
      [1.7, 1318.5],
      [2.4, 1108.7],
      [3.1, 1760],
      [4.0, 880],
      [4.6, 1318.5],
      [5.5, 2093],
      [6.3, 1108.7],
    ]) {
      const oscillator = audioContext.createOscillator()
      const gain = audioContext.createGain()

      oscillator.type = 'sine'
      oscillator.frequency.value = frequency
      gain.gain.setValueAtTime(0, t0 + at)
      gain.gain.linearRampToValueAtTime(0.3, t0 + at + 0.03)
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + at + 0.7)
      oscillator.connect(gain)
      gain.connect(master)
      oscillator.start(t0 + at)
      oscillator.stop(t0 + at + 0.8)
    }

    beginPlaying(duration * 1000)
  }

  /**
   * A recorded voice clip, routed through the same analyser so the mesh reacts
   * to it. Same-origin only — a cross-origin stream reads as silence, so a
   * YouTube embed could never drive this (REDESIGN.md §4).
   */
  async function playClip(url) {
    if (playing) return

    const master = await ensureAudio()

    if (master === null) return

    const element = new Audio(url)
    element.crossOrigin = 'anonymous'

    const source = audioContext.createMediaElementSource(element)
    source.connect(master)

    element.addEventListener('ended', () => {
      playing = false
      mode = state
      orbEl.className = `orb orb--${state}`
      readoutEl.textContent = restingReadout
    })

    element.play().then(
      () => beginPlaying(Number.isFinite(element.duration) ? element.duration * 1000 : 60000),
      () => {
        // Autoplay refused: the tap handler will try again on the next tap.
      },
    )
  }

  // ---- the frame -----------------------------------------------------------

  function frame(time) {
    context2d.clearRect(0, 0, 420, 420)

    let bass = 0
    let mid = 0
    let high = 0
    let spectrum = null

    if (playing && analyser) {
      analyser.getByteFrequencyData(frequencies)

      let b = 0
      let m = 0
      let h = 0

      for (let i = 1; i < 9; i += 1) b += frequencies[i]
      bass = b / 8 / 255
      for (let i = 9; i < 48; i += 1) m += frequencies[i]
      mid = m / 39 / 255
      for (let i = 48; i < 100; i += 1) h += frequencies[i]
      high = h / 52 / 255

      spectrum = frequencies
    }

    if (tap > 0) tap *= 0.94

    drawMesh(context2d, points, links, { time, bass, mid, high, tap, spectrum, playing, reduced })

    frameHandle = window.requestAnimationFrame(frame)
  }

  frameHandle = window.requestAnimationFrame(frame)

  // ---- speaking ------------------------------------------------------------

  function say(line, { sound = false } = {}) {
    stopTyping()
    stopTyping = typeInto(lineEl, line, { reduced })

    if (!sound) return

    // A recorded voice replaces the chime rather than joining it.
    if (audioUrl) {
      playClip(audioUrl)
    } else {
      playTransmission()
    }
  }

  /** The line arrives; if it is new, it arrives out loud, and once. */
  function arrive() {
    say(currentLine, { sound: deliver })

    if (deliver) {
      onDelivered?.()
    }
  }

  if (greet) {
    stopTyping = typeInto(greetingEl, salutation(firstName), {
      reduced,
      onDone: () => {
        timers.push(window.setTimeout(arrive, SITUATIONAL_DELAY))
      },
    })
  } else {
    arrive()
  }

  // ---- tap -----------------------------------------------------------------

  // A tap replays whatever the Orb is currently holding.
  orbEl.addEventListener('click', () => {
    tap = 1

    if (navigator.vibrate) navigator.vibrate(10)

    say(currentLine, { sound: true })
  })

  return {
    say,
    setState(next) {
      mode = next
      orbEl.className = `orb orb--${next}`
    },
    get state() {
      return mode
    },
    destroy() {
      window.cancelAnimationFrame(frameHandle)
      stopTyping()
      timers.forEach((timer) => window.clearTimeout(timer))
      timers = []
      audioContext?.close?.()
    },
  }
}

/**
 * The 26px core-only orb that sits in a back pill. No mesh, no canvas — at that
 * size the lattice is noise, and the reference draws it as a lit bead.
 */
export function miniOrb() {
  return '<span class="orb-mini" aria-hidden="true"></span>'
}

/**
 * An Orb that only breathes: the same lattice and the same core, with no audio
 * graph, no line and no tap behind it. The orbit menu puts one at the centre of
 * its ring, where the Orb is the thing you came out of, not something to talk
 * to (NAVIGATION.md §3, §5).
 */
export function mountQuietOrb(container) {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  container.innerHTML = `
    <span class="orb__core" aria-hidden="true"></span>
    <canvas class="orb__mesh" width="420" height="420" aria-hidden="true"></canvas>
  `

  const context2d = container.querySelector('.orb__mesh').getContext('2d')
  const points = lattice()
  const links = edges(points)

  let frameHandle = null

  function frame(time) {
    context2d.clearRect(0, 0, 420, 420)
    drawMesh(context2d, points, links, {
      time,
      bass: 0,
      mid: 0,
      high: 0,
      tap: 0,
      spectrum: null,
      playing: false,
      reduced,
    })

    frameHandle = window.requestAnimationFrame(frame)
  }

  frameHandle = window.requestAnimationFrame(frame)

  return {
    destroy() {
      window.cancelAnimationFrame(frameHandle)
    },
  }
}
